import React from 'react';
import { message, notification } from 'antd';
import { calculateDateRange, calculateLedgerDateRange, resolveLedgerRenderZone } from '../../utils/dates';
import { downloadBlob } from '../../utils/exportUtils';
import { EXPORT_SCOPE_KEYS, EXPORT_COPY, EXPORT_POLL_INTERVAL_MS, EXPORT_POLL_TIMEOUT_MS } from './reports.constants';
import {
    EXPORT_RESULT,
    EXPORT_JOB_STATUS,
    EXPORT_JOB_TERMINAL_STATUSES,
    mapExportError,
} from '../../utils/exportResponse';

/**
 * Server-side export of the Reports screen (KAN-548 / KAN-614), composed into
 * `ReportsController` via `Inherit` next to `ReportsExportDelegate` — the same
 * controller/delegate stacking as the Transactions tab. The delegate holds
 * the header handler (`requestExport`) and the two state setters; everything
 * that talks to the data layer, sequences an outcome or drives the
 * notification lifecycle lives here.
 *
 * The browser never builds the file any more: `performExport(scope)` sends
 * the active tab's filters, sort and — for `page` — the current page/pageSize
 * to `POST /api/admin/reports/:aba/export` and reacts to the three outcomes:
 *
 *  - file  → save it (`downloadBlob`)
 *  - empty → `message.warning`, no file
 *  - job   → a sticky "Preparing…" notification (updated in place with the
 *            row count as the job progresses) and poll `GET …/export/:job_id`
 *            every few seconds until the job ends or the wall-clock ceiling
 *            (`EXPORT_POLL_TIMEOUT_MS`) passes. One job at a time: the
 *            delegate refuses a second request while a job is being polled.
 *            Poll state lives here (`_exportPoll`); `state.exportJob` mirrors
 *            it, carrying the tab and scope so the header indicator and every
 *            notification can say what the job covers.
 *
 * The body is built by the SAME functions the two list fetches use —
 * `buildApiFilters` (Users) and `buildTransactionsApiFilters` (Transactions),
 * with the tab's stored sort — so the file cannot disagree with the screen on
 * filters, search or ordering.
 *
 * Host contract (provided by `ReportsController` / `Reports`): `state.activeTab`,
 * `state.filters` / `state.usersSort` / `state.usersPageInfo` (Users),
 * `state.transactionFilters` / `state.transactionsSort` /
 * `state.transactionsSearch` / `state.transactionsTimezone` /
 * `state.transactionsPageInfo` (Transactions), `buildApiFilters(filters)`,
 * `buildTransactionsApiFilters(filters, options)`, `setExporting`,
 * `setExportJob`, `data.exportReport`, `data.fetchReportExportJob`, and
 * optionally `props.notificationApi` (withHooks).
 */
class ReportsExportController {
    /**
     * Whether a tab currently has a resolved date range — the "By date range"
     * option is disabled otherwise rather than exporting everything silently.
     * The Transactions range is resolved in the zone the ledger endpoint
     * declared, exactly as its fetch does.
     * @param {string} [tab] defaults to the active tab
     * @returns {boolean}
     */
    hasActiveDateRange(tab = this.state?.activeTab) {
        if (tab === 'transactions') {
            const f = this.state?.transactionFilters;
            if (!f) return false;
            const zone = resolveLedgerRenderZone(this.state?.transactionsTimezone);
            const { start, end } = calculateLedgerDateRange(f.timeFrame, f.dateRange, zone) || {};
            return !!(start && end);
        }
        const f = this.state?.filters;
        if (!f) return false;
        const { start, end } = calculateDateRange(f.timeFrame, f.dateRange) || {};
        return !!(start && end);
    }

    /**
     * Body for `POST …/export` built from exactly what the screen is showing.
     *
     * Users: `buildApiFilters` output (filters + search + resolved start/end +
     * `sortBy`/`sortDir` from `usersSort`). Transactions:
     * `buildTransactionsApiFilters` output (type / advanceType /
     * reconciliation / start / end / search + the API `sortBy`/`sortDir` that
     * `buildLedgerParams` derives from `transactionsSort`). Paging is lifted
     * out of `filters`; the sort keys move into `sort`; `page` is only sent
     * for the `page` scope.
     *
     * @param {string} scope one of EXPORT_SCOPE_KEYS
     * @returns {{ tab: string, scope: string, filters: Object, sort: Object, page?: { page: number, pageSize: number } }}
     */
    buildExportBody(scope) {
        const { activeTab } = this.state;
        const api = this.buildExportApiParams(activeTab) || {};
        const { page, pageSize, sortBy, sortDir, ...exportFilters } = api;

        // The service ignores keys it does not know, but an explicit `null`
        // (e.g. hasCompleteDDSwitch unset) is noise in the audit row — drop it.
        for (const key of Object.keys(exportFilters)) {
            if (exportFilters[key] === null || exportFilters[key] === undefined || exportFilters[key] === '') {
                delete exportFilters[key];
            }
        }

        // `{}` = the endpoint's default order, which is also what the table
        // shows when no header is sorted (buildLedgerParams omits the default).
        const sort = sortBy ? { sortBy, ...(sortDir ? { sortDir } : {}) } : {};

        const body = { tab: activeTab, scope, filters: exportFilters, sort };
        if (scope === EXPORT_SCOPE_KEYS.PAGE) {
            const pageInfo = activeTab === 'transactions' ? this.state.transactionsPageInfo : this.state.usersPageInfo;
            body.page = {
                page: Number(pageInfo?.current) || 1,
                pageSize: Number(pageInfo?.perPage) || 1,
            };
        }
        return body;
    }

    /**
     * The query object the tab's list fetch would send right now, from the
     * tab's own builder — one translation point per tab.
     * @param {string} tab
     * @returns {Object}
     */
    buildExportApiParams(tab) {
        if (tab === 'transactions') {
            const { transactionFilters, transactionsSort, transactionsSearch } = this.state;
            return this.buildTransactionsApiFilters(transactionFilters, {
                sort: transactionsSort ?? null,
                search: transactionsSearch ?? '',
            });
        }
        return this.buildApiFilters(this.state.filters);
    }

    /**
     * Send the export request for a scope the delegate has already vetted
     * (`requestExport`) and react to its outcome.
     * @param {string} scope one of EXPORT_SCOPE_KEYS
     */
    async performExport(scope) {
        const body = this.buildExportBody(scope);
        this.setExporting(true);
        try {
            const result = await this.data.exportReport(body);
            this.handleExportResult(result, { tab: body.tab, scope: body.scope });
        } catch (error) {
            console.error('Error requesting report export:', error);
            message.error(await mapExportError(error));
        } finally {
            this.setExporting(false);
        }
    }

    /**
     * @param {{ kind: string }} result from `data.exportReport`
     * @param {{ tab: string, scope: string }} target what the request covered
     */
    handleExportResult(result, target) {
        switch (result?.kind) {
            case EXPORT_RESULT.EMPTY:
                message.warning(EXPORT_COPY.EMPTY);
                return;
            case EXPORT_RESULT.FILE:
                if (downloadBlob(result.blob, result.fileName)) {
                    message.success(EXPORT_COPY.DOWNLOADED(result.rowCount));
                } else {
                    message.error(EXPORT_COPY.FAILED_DESCRIPTION);
                }
                return;
            case EXPORT_RESULT.JOB:
                // A 202 without a job id cannot be polled: report it instead of
                // issuing a "Preparing…" card nothing would ever replace.
                if (!result.jobId) {
                    message.error(EXPORT_COPY.FAILED_DESCRIPTION);
                    return;
                }
                this.startExportPolling(result.jobId, { ...target, rowCount: result.rowCount });
                this.notifyExportPreparing(this.state.exportJob);
                return;
            default:
                message.error(EXPORT_COPY.FAILED_DESCRIPTION);
        }
    }

    /**
     * Begin polling an async job. Replaces any poll already running (callers
     * guard against that; see `ReportsExportDelegate.requestExport`).
     * @param {string} jobId
     * @param {{ tab?: string, scope?: string, rowCount?: number|null }} [meta]
     */
    startExportPolling(jobId, { tab = null, scope = null, rowCount = null } = {}) {
        // A replaced job never gets an outcome; close its sticky card too.
        this.stopExportPolling({ dismiss: true });
        if (!jobId) return;

        const job = { jobId, status: EXPORT_JOB_STATUS.QUEUED, tab, scope, rowCount };
        this._exportPoll = { jobId, tab, scope, timer: null, deadline: Date.now() + EXPORT_POLL_TIMEOUT_MS };
        this.setExportJob(job);
        this._exportPoll.timer = setInterval(() => this.pollExportJob(jobId), EXPORT_POLL_INTERVAL_MS);
    }

    /**
     * Clear the poll timer and job state. Safe to call when nothing runs.
     *
     * Not called on a tab change on purpose: the poll survives a tab switch
     * because the completion notification is the only copy of the download
     * link. Only unmount (`ReportsController.cleanup`) drops a running poll.
     *
     * @param {{ dismiss?: boolean }} [options] `dismiss` also closes the sticky
     *   "Preparing…" notification — for unmount, where no outcome will replace it.
     */
    stopExportPolling({ dismiss = false } = {}) {
        const jobId = this._exportPoll?.jobId;
        if (this._exportPoll?.timer) clearInterval(this._exportPoll.timer);
        this._exportPoll = null;
        if (this.state?.exportJob) this.setExportJob(null);
        if (dismiss && jobId) this.dismissNotification(`export-${jobId}`);
    }

    /**
     * One poll tick. Terminal statuses end the poll and notify; a failed
     * request is skipped (the next tick retries). The wall-clock deadline is
     * checked before the in-flight gate so a request that never settles
     * cannot keep the poll alive.
     * @param {string} jobId
     */
    async pollExportJob(jobId) {
        const poll = this._exportPoll;
        if (!poll || poll.jobId !== jobId) return;

        if (Date.now() >= poll.deadline) {
            this.stopExportPolling();
            this.notify('warning', {
                key: `export-${jobId}`,
                message: EXPORT_COPY.TIMED_OUT_TITLE,
                description: EXPORT_COPY.TIMED_OUT_DESCRIPTION,
            });
            return;
        }
        if (poll.inFlight) return;

        poll.inFlight = true;
        let job = null;
        try {
            job = await this.data.fetchReportExportJob(jobId);
        } finally {
            poll.inFlight = false;
        }
        // The poll may have been stopped (timeout / unmount) while awaiting.
        if (!job || this._exportPoll !== poll) return;

        if (!EXPORT_JOB_TERMINAL_STATUSES.includes(job.status)) {
            const previous = this.state?.exportJob ?? {};
            const rowCount = job.row_count ?? previous.rowCount ?? null;
            const next = { jobId, status: job.status, tab: poll.tab, scope: poll.scope, rowCount };
            if (rowCount !== (previous.rowCount ?? null)) {
                this.notifyExportPreparing(next);
            }
            this.setExportJob(next);
            return;
        }

        this.stopExportPolling();
        this.notifyExportOutcome(job, { tab: poll.tab, scope: poll.scope });
    }

    /**
     * Sticky (duration 0) "Preparing…" notification keyed on the job, so a
     * later call with the same key updates it in place instead of stacking
     * and the terminal outcome replaces it.
     * @param {{ jobId: string, tab?: string, scope?: string, rowCount?: number|null }} job
     */
    notifyExportPreparing(job) {
        this.notify('info', {
            key: `export-${job.jobId}`,
            duration: 0,
            message: EXPORT_COPY.PREPARING_TITLE,
            description: EXPORT_COPY.PREPARING_DESCRIPTION(job, job.rowCount),
        });
    }

    /**
     * Completion / empty / failure notification for an async job. Reuses the
     * job's notification key so it replaces the sticky "Preparing…" card.
     *
     * The download link is a server-supplied presigned URL; it is only ever
     * rendered when it is https, otherwise the job is reported as failed.
     *
     * @param {{ job_id: string, status: string, row_count?: number|null, download_url?: string, expires_at?: string, file_name?: string, error?: string }} job
     * @param {{ tab?: string, scope?: string }} target
     */
    notifyExportOutcome(job, target) {
        const key = `export-${job.job_id}`;
        if (job.status === EXPORT_JOB_STATUS.COMPLETED) {
            if (typeof job.download_url === 'string' && job.download_url.startsWith('https://')) {
                this.notify('success', {
                    key,
                    duration: 0,
                    message: EXPORT_COPY.READY_TITLE,
                    description: EXPORT_COPY.READY_DESCRIPTION(target, job.row_count ?? null, job.expires_at),
                    btn: (
                        <a href={job.download_url} download={job.file_name || true} rel="noopener noreferrer">
                            {EXPORT_COPY.READY_ACTION}
                        </a>
                    ),
                });
                return;
            }
            console.error('Export job completed without a usable download_url:', job.job_id);
        }
        if (job.status === EXPORT_JOB_STATUS.EMPTY) {
            this.notify('warning', { key, message: EXPORT_COPY.EMPTY });
            return;
        }
        this.notify('error', {
            key,
            duration: 0,
            message: EXPORT_COPY.FAILED_TITLE,
            description: job.error || EXPORT_COPY.FAILED_DESCRIPTION,
        });
    }

    /**
     * Notification sink: the `withHooks` context-bound API when present (so
     * the toast renders inside the app's theme context), the antd static
     * API otherwise.
     * @param {'info'|'success'|'warning'|'error'} type
     * @param {Object} args antd notification args
     */
    notify(type, args) {
        const api = this.props?.notificationApi || notification;
        const fn = api?.[type] || notification[type];
        fn(args);
    }

    /**
     * Close a notification by key on the same sink `notify` used.
     * @param {string} key
     */
    dismissNotification(key) {
        const api = this.props?.notificationApi || notification;
        const fn = api?.destroy || notification.destroy;
        if (typeof fn === 'function') fn(key);
    }
}

export default ReportsExportController;
